// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

interface RouterLocation {
  pathname: string;
  search?: Record<string, unknown>;
}

/**
 * Navigation utility functions for path and query parameter matching
 */

/**
 * Checks if a location matches a target path, including query parameters.
 * Special handling for shipment paths where detail pages (/shipments/{key})
 * should match the list page (/shipments) based on the type query param.
 *
 * @param targetPath - The target path to match against (e.g., "/projects?status=active")
 * @param currentLocation - React Router location object
 * @returns true if the current location matches the target path
 *
 * @example
 * // Current: /projects?status=active
 * isCurrentPath("/projects?status=active", location) // true
 * isCurrentPath("/projects?status=upcoming", location) // false
 * isCurrentPath("/projects", location) // false
 *
 * // Special shipment handling:
 * // Current: /shipments/ABC123?type=outbound
 * isCurrentPath("/shipments?type=outbound", location) // true
 */
export function isCurrentPath(
  targetPath: string,
  currentLocation: RouterLocation
): boolean {
  const targetUrl = new URL(targetPath, window.location.origin);

  // Build current URL string properly from React Router location
  let currentUrlString = currentLocation.pathname;

  // Handle object search (React Router's parsed format)
  const currentSearchString = new URLSearchParams();
  Object.entries(currentLocation.search || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      // Handle array values (like statuses: ["active"])
      currentSearchString.set(key, JSON.stringify(value));
    } else {
      currentSearchString.set(key, String(value));
    }
  });

  if (currentSearchString.toString()) {
    currentUrlString += '?' + currentSearchString.toString();
  }

  const currentUrl = new URL(currentUrlString, window.location.origin);

  // Special handling for shipment paths:
  // /shipments/{key}?type=X should match /shipments?type=X
  if (
    targetUrl.pathname === '/shipments' &&
    currentUrl.pathname.startsWith('/shipments')
  ) {
    const targetType = targetUrl.searchParams.get('type');
    const currentType = currentUrl.searchParams.get('type');

    // If target specifies a type, current must have the same type
    if (targetType) {
      return targetType === currentType;
    }

    // If target doesn't specify type, just check path prefix
    return true;
  }

  // Check if target has query parameters
  const targetHasQueryParams = targetUrl.searchParams.toString().length > 0;

  if (targetHasQueryParams) {
    // With query params: require exact pathname match and all query params must match
    if (targetUrl.pathname !== currentUrl.pathname) {
      return false;
    }
    for (const [key, value] of targetUrl.searchParams) {
      if (currentUrl.searchParams.get(key) !== value) {
        return false;
      }
    }
    return true;
  }

  // Without query params: support prefix matching for child routes
  // e.g., /purchase-orders should match /purchase-orders/PO-123
  const targetPathname = targetUrl.pathname;
  const currentPathname = currentUrl.pathname;

  if (currentPathname === targetPathname) {
    return true;
  }

  // Check if current path is a child of target path
  // Ensure proper boundary to avoid /purchase-orders-new matching /purchase-orders
  if (
    currentPathname.startsWith(targetPathname) &&
    currentPathname.charAt(targetPathname.length) === '/'
  ) {
    return true;
  }

  return false;
}
